import * as THREE from 'three';
import type { PhysicsCity, Hit } from '../physics/world';
import type { Population } from './population';
import type { Soldier } from './military';
import type { WalkNode } from './walking';

interface Planted {
  soldier: Soldier;
  cell: number;
  offset: THREE.Vector3;
  mesh: THREE.Mesh;
  escape: WalkNode;
  age: number;
  still: number;
  last: THREE.Vector3;
  epoch: number;
}
/** Fictional sandbox charges reuse the C4 blast/heat/structural path. An actual
 * reachable escape is required before planting, and the original planter plus
 * nearby friendly troops must physically clear the radius before triggering. */
export class CombatCharges {
  readonly active = new Map<string, Planted>();
  planted = 0;
  detonated = 0;
  aborted = 0;
  private geometry = new THREE.BoxGeometry(0.38, 0.18, 0.5);
  private material = new THREE.MeshStandardMaterial({ color: 0xbd704a, roughness: 0.65 });
  private serial = 0;
  constructor(
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {}
  private release(id: string): void {
    const c = this.active.get(id);
    if (!c) return;
    c.mesh.removeFromParent();
    this.physics.pinnedCells.delete(c.cell);
    this.population.hazards = this.population.hazards.filter((h) => h.id !== `event-charge:${id}`);
    this.active.delete(id);
  }
  cancel(): void {
    for (const id of this.active.keys()) this.release(id);
  }
  reset(): void {
    this.cancel();
    this.planted = this.detonated = this.aborted = this.serial = 0;
  }
  private rotation(id: number): THREE.Quaternion | undefined {
    const owner = this.physics.cellOwner.get(id);
    if (owner === undefined) return;
    const a = this.physics.clusters.get(owner),
      d = this.physics.dormant.get(owner);
    return a
      ? new THREE.Quaternion().copy(a.body.rotation())
      : d
        ? new THREE.Quaternion().copy(d.rotation)
        : undefined;
  }
  tryPlant(soldier: Soldier): boolean {
    const p = soldier.person,
      network = this.population.civil?.network;
    if (
      !network ||
      this.active.size >= 12 ||
      this.active.has(p.id) ||
      p.carrier ||
      p.fatal ||
      ['incapacitated', 'falling', 'flee'].includes(p.state) ||
      !this.physics.ground(p.p, 0.15, 0.2)
    )
      return false;
    let hit: Hit | undefined;
    const origin = p.p.clone().add(new THREE.Vector3(0, 0.95, 0));
    for (let direction = 0; direction < 8; direction++) {
      const a = (direction * Math.PI) / 4,
        h = this.physics.ray(origin, new THREE.Vector3(Math.cos(a), 0, Math.sin(a)), 1.65, undefined, {
          physical: true,
          ignoreEntities: true,
        });
      const building = h?.cluster?.building ?? h?.dormant?.building;
      if (
        h &&
        building &&
        building.category !== 'prop' &&
        building.category !== 'bridge' &&
        Math.abs(h.normal.y) < 0.3
      ) {
        hit = h;
        break;
      }
    }
    if (!hit) return false;
    const escape = network
      .near(p.p, 20, p.layer)
      .filter((n) => Math.hypot(n.x - hit!.point.x, n.z - hit!.point.z) > 11.5)
      .sort((a, b) => Math.hypot(a.x - p.p.x, a.z - p.p.z) - Math.hypot(b.x - p.p.x, b.z - p.p.z))
      .slice(0, 24)
      .find((n) => {
        if (this.population.hazardAt(n) || !this.population.footClear(p, n)) return false;
        const move = new THREE.Vector3(n.x - p.p.x, n.y - p.p.y, n.z - p.p.z);
        if (this.physics.sweepFoot(p.p, move) < 0.999) return false;
        for (let step = 1; step <= 12; step++)
          if (!this.physics.ground(p.p.clone().addScaledVector(move, step / 12), 0.2, 0.3)) return false;
        return true;
      });
    if (!escape) return false;
    const cluster = hit.cluster ?? hit.dormant!;
    let chosen: number | undefined,
      distance = Infinity;
    for (const cell of cluster.cells.values()) {
      const at = this.physics.cellPosition(cell.id);
      if (!at) continue;
      const d = at.distanceToSquared(hit.point);
      if (d < distance) {
        distance = d;
        chosen = cell.id;
      }
    }
    if (chosen === undefined) return false;
    const centre = this.physics.cellPosition(chosen)!,
      q = this.rotation(chosen)!,
      mesh = new THREE.Mesh(this.geometry, this.material);
    mesh.position.copy(hit.point).addScaledVector(hit.normal, 0.1);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), hit.normal);
    this.physics.scene.add(mesh);
    this.physics.pinnedCells.add(chosen);
    this.population.civil?.cancelTask(p, 'Original planter following a physically checked escape');
    p.controlled = true;
    p.state = 'planting';
    const charge: Planted = {
      soldier,
      cell: chosen,
      offset: mesh.position.clone().sub(centre).applyQuaternion(q.invert()),
      mesh,
      escape,
      age: 0,
      still: 0,
      last: p.p.clone(),
      epoch: this.physics.life.epoch,
    };
    this.active.set(p.id, charge);
    this.planted++;
    this.serial++;
    this.population.hazards.push({
      id: `event-charge:${p.id}`,
      p: mesh.position.clone(),
      radius: 9,
      life: 30,
    });
    return true;
  }
  update(dt: number): void {
    for (const [id, c] of this.active) {
      const p = c.soldier.person,
        centre = this.physics.cellPosition(c.cell),
        q = this.rotation(c.cell);
      if (
        c.epoch !== this.physics.life.epoch ||
        !centre ||
        !q ||
        p.fatal ||
        ['incapacitated', 'falling'].includes(p.state)
      ) {
        this.aborted++;
        this.release(id);
        continue;
      }
      c.mesh.position.copy(c.offset).applyQuaternion(q).add(centre);
      c.age += dt;
      const hazard = this.population.hazards.find((h) => h.id === `event-charge:${id}`);
      if (hazard) {
        hazard.p.copy(c.mesh.position);
        hazard.life = 2;
      }
      if (c.age < 1.2) {
        p.state = 'planting';
        continue;
      }
      const distance = p.p.distanceTo(c.mesh.position);
      if (distance < 11.5) {
        this.population.walkTo(p, c.escape, dt, 3, true);
        c.still = p.p.distanceToSquared(c.last) < 0.00001 ? c.still + dt : 0;
        c.last.copy(p.p);
        if (c.still > 3 || c.age > 25) {
          this.aborted++;
          this.release(id);
          p.state = 'wait';
        }
        continue;
      }
      let ally = false;
      this.population.actorsNear(c.mesh.position, 10, (a) => {
        if (
          a.category === 'person' &&
          (a as typeof p).faction === p.faction &&
          !a.hidden &&
          !(a as typeof p).fatal
        )
          ally = true;
      });
      if (ally) {
        if (c.age > 25) {
          this.aborted++;
          this.release(id);
        }
        continue;
      }
      const point = c.mesh.position.clone();
      this.release(id);
      p.state = 'wait';
      this.physics.blast(point, 9, 7.3, true, 'c4', 0.85);
      this.population.effects.burst(point, 0xc8baa7, 28, 7);
      this.population.audio.play('blast', 0.8, point);
      this.detonated++;
    }
  }
  snapshot(): Record<string, number> {
    return {
      invasionCharges: this.active.size,
      invasionChargesPlanted: this.planted,
      invasionChargesDetonated: this.detonated,
      invasionChargesAborted: this.aborted,
    };
  }
  dispose(): void {
    this.cancel();
    this.geometry.dispose();
    this.material.dispose();
  }
}
